<template>
  <div class="talent-pool-filter-container">
    <b-row class="d-flex justify-content-between align-items-center mx-1">
      <div class="d-flex flex-row">
        <h4>
          <template v-if="loading">Loading...</template>
          <template v-else>{{ results.edges.length | plural('result') }}</template>
        </h4>
      </div>
      <div class="d-flex flex-row">
        <sort class="mr-4" :options="talentOrderByEnums" :value="order" @input="sortResults($event)"/>
        <toggle-filters class="py-1 px-3" v-model="showHorizontalWhere"/>
      </div>
    </b-row>
    <b-row class="mt-3" v-if="showHorizontalWhere">
      <b-col>
        <filter-block :fields="filterFields" :value="where" @input="filterResults($event)" />
      </b-col>
    </b-row>

    <!-- matched users card -->
    <b-card class="mt-3" v-if="results && Array.isArray(results.edges)">
      <b-row class="pb-3 border-bottom">
        <b-col class="block-title">Talent Pool</b-col>
        <b-col v-if="!job.isInternal && $root.isReferralsFeatureEnabled">
          <b-button
            v-if="!job.isInternal && $root.isReferralsFeatureEnabled"
            class="float-right"
            variant="primary"
            :to="{ name: 'JobShow', params: { jobID: job.id } }"
          >
            <font-awesome-icon icon="link"/> Referrals Talent Pool
          </b-button></b-col>
      </b-row>
      <b-row>
        <b-col class="px-0">
          <b-table
            table-class="m-0"
            hover responsive show-empty
            :fields="fields"
            :items="results.edges"
            :per-page="10"
            :current-page="page"
            :busy="false"
          >
            <template #table-busy>
              <div class="text-center my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Loading...</strong>
              </div>
            </template>

            <template #cell(avatar)="{ item }">
              <avatar :user="item" :size="30"/>
            </template>

            <template #cell(skills)="{ item: { skills: talentSkills } }">
              <div v-if="Array.isArray(talentSkills)">
                <span
                  class="skill-tag mr-2 mt-1"
                  v-for="skill in talentSkills"
                  :style="{ backgroundColor: skill.background }"
                  :key="skill.name"
                >{{ skill.name }}</span>
              </div>
            </template>

            <template #cell(interest)="{ item }">
              <div>
                <span
                  class="skill-tag mr-2 mt-1"
                  v-for="interest in talentInterests(item)"
                  :style="{ backgroundColor: interest.isMatch ? matchBG : '#eee' }"
                  :key="interest.label"
                >{{ interest.label }}</span>
              </div>
            </template>

            <template #cell(buttons)="{ item: { user: talent } }">
              <b-link
                variant="primary"
                @click="selectedTalent = talent"
                v-b-modal="'profile-show-modal'"
              >Profile</b-link>
              <b-button
                variant="primary"
                class="ml-2"
                @click="selectedTalent = talent"
                v-b-modal="'profile-contact-modal'"
              >Contact</b-button>
            </template>
          </b-table>

          <b-card class="mt-3 border-0" body-class="d-flex flex-row p-3 justify-content-center align-items-center">
            <b-pagination
              class="m-0"
              v-model="page"
              :per-page="limit"
              :total-rows="total"
            />
          </b-card>
        </b-col>
      </b-row>
    </b-card>

    <!-- User Profile Modal -->
    <b-modal
      id="profile-show-modal"
      title="Profile"
      body-class="gray-bg"
      hide-footer
      lazy
    >
      <profile-show
        :user="selectedTalent"
        layout="modal"
      />
    </b-modal>

    <!-- User Profile Contact Modal -->
    <b-modal
      id="profile-contact-modal"
      :title="`Contact ${selectedTalent ? selectedTalent.fullName : ''}`"
      body-class="gray-bg"
      hide-footer
      lazy
    >
      <b-form-group label-for="subject">
        <h4><b>From Name</b></h4>
        <p class="text-muted">Edit the name of the sender.</p>
        <b-form-input
          type="text"
          v-model="contactForm.fromName"
        ></b-form-input>
      </b-form-group>
      <b-form-group label-for="subject">
        <h4><b>Subject</b></h4>
        <p class="text-muted">If you select "Recommend this opening" your message will be sent along with a link to this job.</p>
        <b-form-select
          :options="[
            { value: 'RECOMMEND_JOB', text: 'Recommend this opening' },
            { value: 'CONTACT_USER', text: 'Contact user' },
          ]"
          v-model="contactForm.subject"
        ></b-form-select>
      </b-form-group>
      <b-form-group>
        <h4><b>Message</b></h4>
        <p class="text-muted">This text will be sent to {{ selectedTalent ? selectedTalent.fullName : '' }} as an email from the platform.</p>
        <tip-tap-editor ref="tipTapEditor" :value="contactForm.message" @input="value => contactForm.message = value"/>
      </b-form-group>
      <b-button
        variant="primary"
        class="float-right"
        @click="sendEmail"
      >
        Send Email
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import Avatar from '@/components/users/Avatar';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import FilterBlock from '@/components/misc/FilterBlock';
import Sort from '@/components/misc/Sort';
import ToggleFilters from '@/components/misc/ToggleFilters';
import ProfileShow from '@/components/profile/ProfileShow';
import TipTapEditor from '@/components/misc/TiptapEditor';
import { mapRoot } from '@src/mixins/root';
import tinycolor from 'tinycolor2';
import {
  talentToCurrentJobMatchScore,
  talentToCurrentJobMatchInterestScore,
  talentInterestMatchDetails
} from '@src/lib/imMatching';

const defaultEmailContent = ctx => `
  <p>Hi ${ctx.selectedTalent ? ctx.selectedTalent.firstName : ''},</p>
  <p>I believe you would be a great match for this opening.</p>
  <p>You can take a look at this opportunity by clicking the button below.</p>
  <p>Best,</p>
  <p>Your Hiring Team</p>
`;

export default {
  name: 'InternalTalentPoolContainer',
  props: {
    loading: {
      type: Boolean,
      default: () => false,
    },
    hidePagination: {
      type: Boolean,
      default: () => false,
    },
    defaultShowFilters: {
      type: Boolean,
      default: () => true,
    },
    talents: {
      type: Array,
      default: () => ([]),
    },
    job: {
      type: Object,
      default: () => null,
    },
    talentOrderByEnums: {
      type: Object,
      default() {
        return {
          'CREATED_DESC': 'Newest first',
          'CREATED_ASC': 'Oldest first',
          'MOST_RELEVANT': 'Most relevant',
          'MOST_RELEVANT_INTEREST': 'Most relevant based on interest',
        };
      }
    },
    filterFields: {
      type: Array,
      default() {
        const filterFields = [
          {
            key: 'title',
            type: 'STRING',
            name: 'Title',
          },
          {
            key: 'skills',
            type: 'JOB_SKILLS',
            name: 'Skills',
          },
          {
            key: 'location',
            type: 'LOCATION',
            name: 'Location',
          },
        ];
        return filterFields;
      },
    },
  },
  components: {
    Avatar,
    Sort,
    ToggleFilters,
    FilterBlock,
    ProfileShow,
    LoadingSpinner,
    TipTapEditor,
  },
  mounted() {
    this.sortResults(this.order);
  },
  watch: {
    talents() {
      this.results.edges = Array.isArray(this.talents) ? [ ...this.talents ] : [];
      this.sortResults(this.order);
      this.total = (this.talents ? this.talents : []).length;
      this.totalPages = Math.ceil((this.talents ? this.talents : []).length / 10);
    },
    selectedTalent() {
      this.contactForm.message = defaultEmailContent(this);
    },
    contactForm: {
      deep: true,
      handler() {
        if (this.contactForm.subject === 'RECOMMEND_JOB') {
          if (this.contactForm.message.trim() === '') {
            this.contactForm.message = defaultEmailContent(this);
            this.$refs.tipTapEditor.setContent(defaultEmailContent(this));
          }
        } else {
          if (this.contactForm.message === defaultEmailContent(this)) {
            this.contactForm.message = '';
            this.$refs.tipTapEditor.setContent('');
          }
        }
      },
    },
  },
  data() {
    return {
      results: {
        edges: this.talents ? this.talents : []
      },
      showHorizontalWhere: true,
      where: {
        title: '',
        skills: [],
        location: null,
      },
      order: 'MOST_RELEVANT',
      page: 1,
      limit: 10,
      total: (this.talents ? this.talents : []).length,
      totalPages: Math.ceil((this.talents ? this.talents : []).length / 10),
      selectedTalent: null,
      contactForm: {
        subject: 'RECOMMEND_JOB',
        message: '',
        fromName: 'Hiring Team',
      }
    };
  },
  computed: {
    ...mapRoot([ 'company', 'user' ]),
    matchBG() {
      const { siteBranding } = this.company;
      return siteBranding && siteBranding.primaryColour ? tinycolor(siteBranding.primaryColour).lighten(34).toString() : '#eee';
    },
    fields() {
      return [
        {
          key: 'avatar',
          label: '',
          headerTitle: 'Avatar',
          thStyle: { width: '50px' },
        },
        {
          key: 'fullName',
          label: 'Name',
          thStyle: { width: '120px' },
        },
        {
          key: 'jobTitle',
          label: 'Job Title',
          thStyle: { width: '150px' },
        },
        {
          key: 'skills',
          label: 'Skills',
          thStyle: { width: '180px' },
        },
        {
          key: 'interest',
          label: 'Interest',
          thStyle: { width: '180px' },
        },
        {
          key: 'buttons',
          label: '',
          thStyle: { width: '100px' },
        },
      ];
    },
  },
  methods: {
    sortResults(order) {
      console.log(order);
      this.order = order;
      this.results.edges = this.results.edges.sort((a, b) => {
        if (order === 'MOST_RELEVANT') {
          return -1 * (talentToCurrentJobMatchScore.call(this, a) - talentToCurrentJobMatchScore.call(this, b));
        }
        if (order === 'MOST_RELEVANT_INTEREST') {
          return -1 * (talentToCurrentJobMatchInterestScore.call(this, a) - talentToCurrentJobMatchInterestScore.call(this, b));
        }
        if (order === 'CREATED_ASC') {
          return a.updatedAt - b.updatedAt;
        }
        if (order === 'CREATED_DESC') {
          return b.updatedAt - a.updatedAt;
        }
        return 0;
      });
    },
    filterResults(where) {
      this.where = where;
      this.results.edges = this.talents.filter(talent => {
        let result = true;
        if (where.title && where.title.length > 1) {
          result = result && (talent.jobTitle ? talent.jobTitle : '').toLowerCase().includes(where.title.toLowerCase());
        }
        if (Array.isArray(where.skills) && where.skills.length > 0) {
          let talentSkills = Array.isArray(talent.rawSkills) ? talent.rawSkills : [];
          if (this.job && this.job.isDevelopment) {
            const talentDevelopmentSkills = Array.isArray(talent.rawDevelopmentSkills) ? talent.rawDevelopmentSkills : [];
            talentSkills = [
              ...talentSkills,
              ...talentDevelopmentSkills,
            ];
          }
          result = result && Array.isArray(talentSkills) && talentSkills.length > 0 && where.skills.reduce(
            (out, skill) => talentSkills.includes(skill) && out, true
          );
        }
        if (where.location && where.location.id) {
          result = result && talent.location && talent.location.id && where.location.id === talent.location.id;
        }
        return result;
      });
      this.sortResults(this.order);
      this.total = (this.results.edges ? this.results.edges : []).length;
      this.totalPages = Math.ceil((this.results.edges ? this.results.edges : []).length / 10);
    },
    sendEmail() {
      this.$emit('sendContactEmail', { contactForm: this.contactForm, talent: this.selectedTalent });
      this.$bvModal.hide('profile-contact-modal');
    },
    talentInterests(talent) {
      const interests = talentInterestMatchDetails.call(this, talent);
      return Object.keys(interests).reduce((out, key) => {
        if (Array.isArray(interests[key])) {
          interests[key].forEach(el => {
            out.push(el);
          });
        }
        return out;
      }, []);
    }
  },
};
</script>



// WEBPACK FOOTER //
// src/components/projects/InternalTalentPoolContainer.vue